import './Header.css'

import React, { useEffect, useRef } from 'react'

import { Skeleton } from 'primereact/skeleton'

import { DocumentTitle } from '../DocumentTitle'
import { NavigateBackButton } from '../NavigateBackButton'
import { PrintHandler } from '../PrintHandler'

/**
 * Header Component
 *
 * Represents the header of a page or section. Includes a title, optional additional content,
 * navigate back button, and actions. Can have a sticky behavior based on the `isSticky` prop.
 *
 * @component
 * @param {string} [title] - The main title of the header.
 * @param {JSX.Element} [titleContent] - Additional content to be displayed alongside the title.
 * @param {boolean} [isLoading=false] - Boolean indicating whether the header content is loading.
 * @param {boolean} [isSticky=true] - Boolean indicating whether the header should have a sticky behavior.
 * @param {boolean} [showBackButton=false] - Boolean indicating whether to show a back navigation button.
 * @param {string} [backUrl] - URL for the back navigation button.
 * @param {boolean} [showPrintButton=false] - Boolean indicating whether to show a print button.
 * @param {JSX.Element} [actions] - Additional buttons to be displayed in the header.
 * @returns {JSX.Element} Header component
 */

type Props = {
  title?: string
  titleContent?: JSX.Element
  isLoading?: boolean
  isSticky?: boolean
  showBackButton?: boolean
  backUrl?: string
  showPrintButton?: boolean
  actions?: JSX.Element
}

export const Header: React.FC<Props> = ({
  title,
  titleContent,
  isLoading = false,
  isSticky = true,
  showBackButton = false,
  backUrl,
  showPrintButton = false,
  actions,
}) => {
  const scrollWatcherRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isSticky === false) return

    const primaryHeader = document.querySelector('.primary-header')
    const headerObserver = new IntersectionObserver(
      entries => {
        primaryHeader?.classList.toggle('sticking', !entries[0].isIntersecting)
      },
      { threshold: 0 },
    )

    if (scrollWatcherRef.current) {
      headerObserver.observe(scrollWatcherRef.current)
    }

    return () => {
      headerObserver.disconnect()
    }
  }, [isSticky])

  const skeleton = <Skeleton height="30px" />

  return (
    <>
      {/* Title in the tab of the browser */}
      <DocumentTitle title={title} />

      {/* Scroll-Watcher für isSticky */}
      {isSticky && <div ref={scrollWatcherRef} data-scroll-watcher="" />}

      <div className="primary-header">
        <div className="primary-header__inner">
          <div className="primary-header__title w-full">
            {/* Back button */}
            {showBackButton && <NavigateBackButton url={backUrl} />}

            {/* Title */}
            <h1 className="text-lg lg:text-2xl w-full">
              {isLoading ? skeleton : title}
              {titleContent}
            </h1>
          </div>

          {/* Actions */}
          <div className="primary-header__actions flex flex-shrink-0 gap-2 ">
            {showPrintButton && <PrintHandler />}
            {actions}
          </div>
        </div>
      </div>
    </>
  )
}
